<template>
  <div class="business-date-picker">
    <v-date-picker
      v-show="!isDatePickerLoading"
      ref="datePicker"
      :value="value"
      :allowed-dates="isAllowedDate"
      :disabled="disabled || isDatePickerLoading"
      v-bind="$attrs"
      v-on="$listeners"
      @update:picker-date="onPickerDateChange"
    >
      <slot />
    </v-date-picker>
    <div
      v-if="isDatePickerLoading"
      class="business-date-picker__loading"
    >
      <v-progress-circular
        indeterminate
        color="grey"
        :size="60"
        :width="4"
        class="v-progress-circular--visible"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isAllowedBusinessDate } from '@/helpers/BusinessDateHelper';

export default {
  name: 'BusinessDatePicker',
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Array],
      default: null,
    },
    allowedDates: {
      type: Function,
      default: null,
    },
    unavailableDates: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    holidaysLoading: false,
  }),
  computed: {
    ...mapState({
      holidaysDateToNameMap: (state) => state.holidaysDateToNameMap,
    }),
    isDatePickerLoading() {
      return this.isLoading || this.holidaysLoading;
    },
    unavailableDatesSet() {
      return new Set(this.unavailableDates);
    },
  },
  async created() {
    this.holidaysLoading = true;
    try {
      await this.$store.dispatch('getHolidays');
    } finally {
      this.holidaysLoading = false;
    }
  },
  methods: {
    isAllowedDate(date) {
      if (this.isDatePickerLoading) return false;
      if (!isAllowedBusinessDate(date, this.holidaysDateToNameMap)) return false;
      if (this.unavailableDatesSet.has(date)) return false;
      return this.allowedDates ? this.allowedDates(date) : true;
    },
    onPickerDateChange(pickerDate) {
      this.emitVisibleDateRange(pickerDate);
    },
    emitVisibleDateRange(date) {
      const pickerMonth = this.getPickerMonth(date);

      if (!pickerMonth) return;

      const [year, month] = pickerMonth.split('-').map((value) => parseInt(value, 10));
      const endDay = new Date(year, month, 0).getDate();

      this.$emit('visible-date-range-change', {
        picker_date: pickerMonth,
        start_date: `${pickerMonth}-01`,
        end_date: `${pickerMonth}-${String(endDay).padStart(2, '0')}`,
      });
    },
    getPickerMonth(date) {
      const selectedDate = Array.isArray(date) ? date[0] : date;

      if (selectedDate && typeof selectedDate === 'string') return selectedDate.substring(0, 7);

      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');

      return `${today.getFullYear()}-${month}`;
    },
  },
};
</script>

<style scoped>
.business-date-picker {
  position: relative;
  min-width: 290px;
}

.business-date-picker__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 290px;
  min-height: 290px;
  background-color: var(--v-background-base, #fff);
  cursor: progress;
}
</style>
